import { call, put, select, takeEvery } from "redux-saga/effects";
import { createAction } from "redux-actions";
import axios from "axios";
import moment from "moment";
import { stringify } from "../../../../utils/stringify";
import { queryParams } from "../../../../utils/queryParams";
import { API_DATE_FORMAT } from "../../../../app_constants";

export const SET_LOADING = "saga/referrals/SET_LOADING";
export const SET_CONSTANTS = "saga/SET_CONSTANTS";
export const FETCH_MODULE = "saga/referrals/FETCH_MODULE";
export const TOGGLE_VIEW = "saga/referrals/TOGGLE_VIEW";
export const FETCH_FILTERS = "saga/referrals/FETCH_FILTERS";
export const UPDATE_REFERRAL_TOGGLE = "saga/referrals/UPDATE_REFERRAL_TOGGLE";
export const UPDATE_FILTERS = "saga/referrals/UPDATE_FILTERS";
export const SET_FILTERS_LOADING = "saga/referrals/SET_FILTERS_LOADING";
export const SET_ALL_SITES_MODE = "saga/referrals/SET_ALL_SITES_MODE";
export const UPDATE_REFERRAL_CATEGORIES = "saga/referrals/UPDATE_REFERRAL_CATEGORIES";
export const UPDATE_MODULE = "saga/referrals/UPDATE_REFERRAL_CATEGORIES";
export const RESET_MODULE = "saga/referrals/RESET_MODULE";
// keep it small to match reducer names and tags from backend props
export const REFERRAL_BY_SOURCE = "referralBySource";
export const TOTAL_REVENUE = "totalRevenue";

const modulesWithAllSitesLogic = ["referralBySource", "totalRevenue"];

export const actionCreators = (moduleName) => ({
  fetchData: createAction(FETCH_MODULE + moduleName),
  updateData: createAction(UPDATE_MODULE + moduleName),
  toggleView: createAction(TOGGLE_VIEW + moduleName),
  updateFilters: createAction(UPDATE_FILTERS + moduleName),
  updateReferralToggle: createAction(UPDATE_REFERRAL_TOGGLE + moduleName),
  resetData: createAction(RESET_MODULE + moduleName)
});

export default function* watchReferrals(moduleName) {
  yield takeEvery(FETCH_MODULE + moduleName, fetchData);
  yield takeEvery(UPDATE_MODULE + moduleName, updateData);
  yield takeEvery(TOGGLE_VIEW + moduleName, toggleView);
  yield takeEvery(UPDATE_REFERRAL_TOGGLE + moduleName, updateReferralToggle);
  yield takeEvery(UPDATE_FILTERS + moduleName, updateFilters);
  yield takeEvery(RESET_MODULE + moduleName, resetData);
}

const METHODS = {
  POST: "post",
  GET: "get"
};

function apiCall(route, params, method) {
  if (method === METHODS.POST) {
    return call(axios.post, queryParams.apiVersion + route, params);
  }
  return call(
    axios.get,
    queryParams.apiVersion +
      route +
      Object.keys(params).reduce(
        (res, propName) => res + propName + "=" + params[propName] + "&",
        "?"
      )
  );
}

function* fetchData({ payload }) {
  const { moduleName, availableAllSitesView } = payload;

  yield put({ type: SET_LOADING + "/" + moduleName, payload: true });
  yield put({
    type: SET_CONSTANTS + "/" + moduleName,
    payload
  });

  // TODO: check
  if (modulesWithAllSitesLogic.includes(moduleName) && !availableAllSitesView) {
    yield put({ type: SET_LOADING + "/" + moduleName, payload: false });
    yield put({ type: SET_ALL_SITES_MODE + "/" + moduleName, payload: true });
  } else {
    yield fetchFiltersAndChart(moduleName);
  }
}

function* fetchFiltersAndChart(moduleName) {
  try {
    const { commonParams, filtersPath, dataPath, referralCategory } =
      yield getApiParams(moduleName);

    let params = {
      ...commonParams,
      referral_category: referralCategory
    };

    const { start_date, end_date } = yield fetchFilters({
      payload: {
        moduleName,
        params,
        filtersPath
      }
    });

    const {
      filters: { referralValues, selectedProspectTypes }
    } = yield select((state) => state[moduleName]);

    params = {
      ...params,
      start_date,
      end_date,
      categories: stringify(referralValues),
      ...(selectedProspectTypes ? { prospect_type_ids: stringify(selectedProspectTypes) } : {})
    };

    const { data } = yield apiCall(dataPath, params, METHODS.POST);

    yield put({
      type: FETCH_MODULE + "/" + moduleName,
      payload: data.data ? data.data : data
    });
  } catch (error) {
    console.error(error);
  }
}

function* resetData({ payload: { moduleName } }) {
  yield put({ type: SET_LOADING + "/" + moduleName, payload: true });

  yield put({
    type: UPDATE_FILTERS + "/" + moduleName,
    payload: { referralCategory: false }
  });

  yield fetchFiltersAndChart(moduleName);
}

function* updateReferralToggle({ payload }) {
  const { moduleName, ...rest } = payload;

  yield put({ type: UPDATE_FILTERS + "/" + moduleName, payload: rest });
  const { commonParams, filtersPath } = yield getApiParams(moduleName);

  const params = {
    ...commonParams,
    referral_category: payload.referralCategory
  };

  yield put({ type: SET_FILTERS_LOADING + "/" + moduleName, payload: true });

  yield fetchFilters({
    payload: {
      moduleName,
      params,
      filtersPath,
      updateReferralCategory: true
    }
  });
  yield put({ type: SET_FILTERS_LOADING + "/" + moduleName, payload: false });
}

function* toggleView({ payload }) {
  const { moduleName, view } = payload;
  yield put({ type: TOGGLE_VIEW + "/" + moduleName, payload: view });
  yield updateData({ payload });
}

function* fetchFilters({ payload }) {
  const { moduleName, params, filtersPath, updateReferralCategory } = payload;
  const {
    data: { filters }
  } = yield apiCall(filtersPath, params);
  if (updateReferralCategory) {
    yield put({ type: UPDATE_REFERRAL_CATEGORIES + "/" + moduleName, payload: filters });
  } else {
    yield put({ type: FETCH_FILTERS + "/" + moduleName, payload: filters });
  }
  return filters;
}

function* updateFilters({ payload }) {
  const { moduleName, ...rest } = payload;

  yield put({ type: UPDATE_FILTERS + "/" + moduleName, payload: rest });
}

function* updateData({ payload }) {
  const { moduleName, filters } = payload;
  yield put({ type: SET_LOADING + "/" + moduleName, payload: true });

  const { commonParams, dataPath, table_view } = yield getApiParams(moduleName);
  try {
    const {
      startDate: start_date,
      endDate: end_date,
      referralCategory,
      referralValues,
      selectedProspectTypes
    } = filters;

    const params = {
      ...commonParams,
      start_date,
      end_date,
      table_view,
      referral_category: referralCategory,
      categories: stringify(referralValues),
      ...(selectedProspectTypes ? { prospect_type_ids: stringify(selectedProspectTypes) } : {})
    };

    const { data } = yield apiCall(dataPath, params, METHODS.POST);

    yield put({
      type: FETCH_MODULE + "/" + moduleName,
      payload: data.data ? data.data : data
    });
  } catch (error) {
    console.error(error);
  }
}

function* getApiParams(moduleName) {
  const { organization_id, short_url } = yield select((state) => state.commonReducer.commonProps);
  const {
    filtersPath,
    calendarEndDate,
    availableAllSitesView,
    dataPath,
    isTable,
    filters: { referralCategory, selectedProspectTypes }
  } = yield select((state) => state[moduleName]);

  const startDate = moment(calendarEndDate)
    .startOf("month")
    .subtract(11, "month")
    .format(API_DATE_FORMAT);
  return {
    commonParams: {
      organization_id: organization_id.toString(),
      short_url,

      start_date: startDate,
      end_date: calendarEndDate
    },
    dataPath,
    referralCategory,
    selectedProspectTypes,
    table_view: isTable,
    filtersPath,
    availableAllSitesView
  };
}
